import React, { useState } from "react";

import { Intent, OverlayToaster, Position, Spinner } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { ManageGuestsDialog } from "@/components/pieces/coaching/meetings/MeetingGuests/components/GuestsManageSection/ManageGuestsDialog";
import {
  canManageGuests,
  canToggleAllowGuests,
} from "@/components/pieces/coaching/meetings/MeetingGuests/components/GuestsManageSection/utils";
import {
  FullMeetingDocument,
  useSaveMeetingAllowGuestsMutation,
} from "@/graphql";
import { FullMeeting } from "@/types/meetings";
import { ensureNonNullArray } from "@/utils/array";

export function ManageGuestsSection({ meeting }: { meeting: FullMeeting }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const toasterRef = React.useRef<OverlayToaster>(null);
  const allowGuests = meeting?.allowGuests;

  const onSuccess = (message: string) => {
    if (!toasterRef.current) return;

    toasterRef.current.show({
      message: message || "Success",
      intent: Intent.SUCCESS,
    });
  };

  const onError = (error: Error) => {
    if (!toasterRef.current) return;

    toasterRef.current.show({
      message: error?.message || "Failed",
      intent: Intent.DANGER,
    });
  };

  const [saveMeetingAllowGuests, { loading: saving }] =
    useSaveMeetingAllowGuestsMutation({
      onCompleted: (data) => {
        const ok = data?.saveMeetingAllowGuests?.ok;
        if (ok) {
          if (data?.saveMeetingAllowGuests?.ok) {
            onSuccess("Update allow guests setting successfully");
          }
        } else {
          onError(new Error("Error saving allow guests"));
        }
      },
      onError,
      refetchQueries: [
        {
          query: FullMeetingDocument,
          variables: { id: meeting?.id },
        },
      ],
      awaitRefetchQueries: true,
    });

  return (
    <>
      {canManageGuests(meeting) &&
        (saving ? (
          <Spinner size={24} />
        ) : (
          <Flex pt={2}>
            {allowGuests && (
              <Box mr={2}>
                <a
                  onClick={(event) => {
                    event.preventDefault();
                    setIsDialogOpen(true);
                  }}
                >
                  {"Manage Guests"}
                </a>
              </Box>
            )}
            {canToggleAllowGuests(meeting) && (
              <a
                onClick={(event) => {
                  event.preventDefault();
                  saveMeetingAllowGuests({
                    variables: {
                      meetingId: meeting?.id,
                      allowGuests: !allowGuests,
                    },
                  });
                }}
              >
                {allowGuests ? "Disable" : "Enable"}
              </a>
            )}
          </Flex>
        ))}

      <ManageGuestsDialog
        meeting={meeting}
        open={isDialogOpen}
        emails={ensureNonNullArray(meeting?.guests)}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={() => onSuccess("Update guests list successfully")}
      />
      <OverlayToaster position={Position.BOTTOM_RIGHT} ref={toasterRef} />
    </>
  );
}
